import React, {FC} from "react";
import {useAxios} from "../../../utils/hooks";
import useSafeState from "../../../utils/safe-state";
import {Trans} from "@lingui/macro";
import {Breadcrumb, Button, Divider, Popconfirm, Tooltip, Upload, UploadProps} from "antd";
import {
    DeleteTwoTone,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons/lib";
import DtoTable from "../../../components/dto-table/dto-table";
import ButtonWithIcon from "../../../components/buttons/button-with-icon";
import ItemModel from "../../../models/items/item-model";
import Drawer from "../../../components/drawer/drawer";
import ItemDrawer from "../../../forms/item-form";
import ItemDataForm from "../../../forms/item-data-form";
import {DownloadOutlined, EditOutlined, FolderOpenOutlined, ContactsOutlined, FileTextOutlined} from "@ant-design/icons";
import {openNotification} from "../../../utils/openNotification";
import FileDownload from "js-file-download";
import ContactDrawer from "../../../forms/contact-form";

const ItemsScreen: FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [trigger, setTrigger] = useSafeState(false);
    const itemText = <Trans>Item</Trans>
    const [itemFileList, setItemFileList] = useSafeState<Array<any>>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);


    const itemDrawer = Drawer(
        {
            title: itemText,
            children:
                <ItemDrawer onClose={() => {
                    setTrigger(function(prev) { return !prev; });
                }}/>,
            customClose: () => setTrigger(function(prev) { return !prev; })
        }
    );

    const [contactDrawerTrigger, setContactDrawerTrigger] = useSafeState(false);
    const contactDrawer = Drawer(
        {
            title: <Trans>Contact</Trans>,
            children: <ContactDrawer onClose={() => {
                setContactDrawerTrigger(!contactDrawerTrigger);
            }}/>,
            customClose: () => setContactDrawerTrigger(!contactDrawerTrigger)
        }
    );

    const handleDelete = (record: any) => {

        if (axiosInstance.current != null) {
            axiosInstance.current
                .delete('/resource/items/id?id=' + encodeURIComponent(record.id)
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully deleted.')
                    setTrigger(function(prev) { return !prev; });
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                    setTrigger(function(prev) { return !prev; });
                });
        }

    };

    const action = (record: any) => {

        return (
            <>
                <Tooltip title={<Trans>Edit</Trans>}>
                    <EditOutlined className={"ActionButtons"} style={{color: "#40a9ff"}}
                                  onClick={() =>
                                      itemDrawer
                                          .open(
                                              "Edit",
                                              {
                                                  id: record.id,
                                                  itemNo: record.itemNo,
                                                  supplierItemNo: record.supplierItemNo,
                                                  name: record.name,
                                                  tarricCode: record.tarricCode,
                                                  tradeName: record.tradeName,
                                                  relevantProducts: record.relevantProducts,
                                                  scientificName: record.scientificName,
                                                  weight: record.weight,
                                                  countryAndRegionOfProduction: record.countryAndRegionOfProduction,
                                                  productionStartDate: record.productionStartDate,
                                                  productionEndDate: record.productionEndDate,
                                                  isSparePart: record.isSparePart,
                                                  supplier: {
                                                    code: record.supplier.code
                                                  },
                                                  version: record.version
                                              })
                                  }
                    />
                </Tooltip>
                <Tooltip title={<Trans>Contacts</Trans>}>
                    <ContactsOutlined className={"ActionButtons"} style={{color: "#52c41a"}}
                                      onClick={() => {
                                          contactDrawer.open('Add new', { itemId: record.id });
                                      }}
                    />
                </Tooltip>
                <Tooltip title={<Trans>Remove</Trans>}>
                    <Popconfirm
                        title={<Trans>Are you sure want to delete record?</Trans>}
                        onConfirm={() => handleDelete(record)}
                        okText={<Trans>Yes</Trans>}
                        cancelText={<Trans>No</Trans>}>
                        <DeleteTwoTone className={"ActionButtons"} twoToneColor={"red"}/>
                    </Popconfirm>

                </Tooltip>

            </>
        )
    };

    const actions = {
        width: 150,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    const handleDownloadXls = () => {
        if (axiosInstance.current != null) {
            const FileDownload = require('js-file-download');
            axiosInstance
                .current({
                    url: process.env.REACT_APP_API_BASE_URL + '/public/file/get?folder=' + 'eudrdocuments' + '&filename=onboarding/EUDR_ITEMS_TEMPLATE.xlsx',
                    method: 'GET',
                    responseType: 'blob',
                })
                .then(response => {
                    FileDownload(response.data, 'EUDR_ITEMS_TEMPLATE.xlsx');
                });
        }
    }

    const itemProps: UploadProps = {
        name: 'file',
        action: process.env.REACT_APP_API_BASE_URL + '/resource/items/upload-giga-xls',
        multiple: false,
        showUploadList: false, // opcionális, ha nem kell lista
        accept: '.xlsx',
        customRequest: async (options) => {
            const { file, onSuccess, onError } = options;
            if (!axiosInstance.current) return;

            setItemLoading(true);
            const formData = new FormData();
            formData.append('file', file as any);

            try {
                await axiosInstance.current.post('/resource/items/upload-giga-xls', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                openNotification('customSuccess', 'File successfully uploaded');
                onSuccess && onSuccess({}, file as any);
            } catch (error) {
                openNotification('error', error);
                onError && onError(error as any);
            } finally {
                setItemLoading(false);
            }
        },
        onChange(info) {
            if (info.file.status === 'done') {
                // frissítés a sikeres feltöltés után
                setTrigger(function(prev) { return !prev; });
            }
        }
    };

    const header = (
        <>
            <Button type={'primary'} onClick={() => itemDrawer.open('Add new')}>
                <ButtonWithIcon icon={<PlusOutlined />} text={<Trans>Add new</Trans>} />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Button onClick={() => handleDownloadXls()}>
                <ButtonWithIcon
                    icon={<DownloadOutlined />}
                    text={<Trans>.xlsx template</Trans>}
                />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Upload {...itemProps}>
                <Button loading={itemLoading}>
                    <ButtonWithIcon icon={<UploadOutlined/>} text={<Trans>.xlsx upload</Trans>} />
                </Button>
            </Upload>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
        </>
    );

    return (<>

            <>

                <Breadcrumb style={{marginBottom: 10}}>
                    <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans>Data Maintenance</Trans></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'Items'}></Trans></Breadcrumb.Item>
                </Breadcrumb>
                <DtoTable
                    tableHeader={header}
                    model={ItemModel}
                    action={actions}
                    trigger={trigger}
                    allowRefreshButton={true}
                    allowExport={true}
                    pageSize={100}
                    scroll={{x: 1300, y: "65vh"}}/>
            </>
            {itemDrawer.component}
            {contactDrawer.component}
        </>
    )
}
export default ItemsScreen;
